import React, { useMemo, useState } from 'react';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
	addProduct,
	updateProduct,
} from '../../../store/actions/admin/products';
import { GetDefaultProduct } from '../../../store/utils/create-event';
import {
	Attribute,
	CreateProduct,
	PageModule,
	Product,
} from '../../../types/working-model';
import ModalComponent from '../../general-ui/modal/modal';
import Icon, { ICONS, COLORS } from '../../general-ui/icon';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import TagSelectInput from '../../general-ui/tag-select/tag-select';
import Textarea from '../../general-ui/textarea/textarea';
import Switch from '../../general-ui/switch/switch';
import EnterSelectInput from '../../general-ui/select/enter-select';
import { InsertProduct, UpdateProduct } from '../../../connection/products';
import { UploadFile } from '../../../connection/uploads';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';

import './edit-products.scss';

type productState = Product | CreateProduct;

interface AddProductsModalProps {
	isOpen: boolean;
	onClose: () => void;
	page_module?: PageModule;
	startingProduct?: productState;
	attributeOptions?: string[];
}

const initialRequiredFields: { [key: string]: Validation } = {
	title: Validation.normal,
	about: Validation.normal,
	image: Validation.normal
};

export default function EditProductsModal({
	isOpen,
	onClose,
	startingProduct = GetDefaultProduct(),
	attributeOptions,
}: AddProductsModalProps): JSX.Element {

	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);

	const [workingProduct, setWorkingProduct] = useState(startingProduct);
	const [uploading, setUploading] = useState(false);
	const [requiredFields, setRequiredFields] = useState(initialRequiredFields);

	const imageRef = useRef<HTMLInputElement | null>(null);
	const dispatch = useDispatch();

	const modalTitle = useMemo(() => {
		if ('product' in workingProduct!) {
			return 'Edit Product';
		} else {
			return 'Add New Product';
		}
	}, [workingProduct]);

	function hasCompletedRequiredFields() {
		const _requiredFields = { ...requiredFields };
		if (!workingProduct?.product_title?.base) _requiredFields.title = Validation.error;
		if (!workingProduct?.description?.base) _requiredFields.about = Validation.error;
		if (!workingProduct?.images.length) _requiredFields.image = Validation.error;

		setRequiredFields(_requiredFields);
		for (const field in _requiredFields) if (_requiredFields[field] === Validation.error) return false;
		return true;
	}

	function resetRequiredFields() {
		const _requiredFields = { ...requiredFields };
		for (const field in _requiredFields) _requiredFields[field] = Validation.normal;
		setRequiredFields(_requiredFields);
	}

	async function finishedAddingProduct() {
		if (!workingProduct.product_title.base) { return; }
		if (!hasCompletedRequiredFields()) { return; }
		const _product = { ...workingProduct } as productState;
		//remove empty attributes
		_product.attributes = _product.attributes?.filter(
			(attribute: Attribute) => attribute.type.trim().length && attribute.value.trim().length
		) ?? [];

		if (!_product.attributes?.length) {
			_product.use_attributes = false;
		}

		//we're editing a product, not creating a new one
		if ('product' in _product) {
			await UpdateProduct(token!, _product);
			dispatch(updateProduct(_product));
			setWorkingProduct(_product);
		} else {
			const newProduct = await InsertProduct(token!, _product);
			dispatch(addProduct(newProduct));
			setWorkingProduct(newProduct);
		}
		onClose();
	}

	function cancel() {
		setWorkingProduct(startingProduct);
		resetRequiredFields();
		onClose();
	}

	function setProductTitle({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		updateProductTransKey('product_title', value);
		setRequiredFields({ ...requiredFields, title: value ? Validation.normal : Validation.error });
	}

	function setBuyNowLink({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setWorkingProduct((product: productState) => {
			if (!product) { return startingProduct; }

			return {
				...product,
				buy_now_link: value,
			};
		});
	}
	function setBuyNowTitle({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		updateProductTransKey('button_text', value);
	}

	function setDescription({
		target: { value },
	}: React.ChangeEvent<HTMLTextAreaElement>) {
		updateProductTransKey('description', value);
		setRequiredFields({ ...requiredFields, about: value ? Validation.normal : Validation.error });
	}

	function setPrice({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		if (maxDecimalPlaces(value)) return;
		updateProductTransKey('price', value);
	}

	function setSalePrice({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		if (maxDecimalPlaces(value)) return;
		updateProductTransKey('sale_price', value);
	}

	function maxDecimalPlaces(value: string) { return value.includes('.') && value.split('.')[1].length > 2; }

	function formatCurrency(key: 'sale_price' | 'price', { target: { valueAsNumber, value } }: React.ChangeEvent<HTMLInputElement>) {
		// toLocalString to handle rounding/formatting
		if (value.length) {
			const localValue = valueAsNumber.toLocaleString('us', { style: 'currency', currency: 'USD' });
			const formatAsNumber = localValue.substring(1).replaceAll(',', '');
			updateProductTransKey(key, formatAsNumber);
		} else {
			updateProductTransKey(key, '');
		}
	}

	function updateProductTransKey(
		key:
			| 'button_text'
			| 'product_title'
			| 'description'
			| 'price'
			| 'sale_price',
		value: string
	) {
		if (!workingProduct) { return; }

		const _workingProduct = { ...workingProduct };

		if (_workingProduct[key]) { _workingProduct[key].base = value; }
		else { _workingProduct[key] = { base: value }; }

		setWorkingProduct(_workingProduct);
	}
	function setUseAttributes(value: any, isOn: boolean) {
		setWorkingProduct((product: productState) => {
			if (!product) { return startingProduct; }

			if (isOn) {
				if (!product.attributes || product.attributes.length < 1) {
					return {
						...product,
						use_attributes: true,
						attributes: [
							{
								type: '',
								value: '',
							},
						],
					};
				} else {
					return {
						...product,
						use_attributes: true,
					};
				}
			} else {
				return {
					...product,
					use_attributes: false,
				};
			}
		});
	}

	function setCategories(categories: string[]) {
		setWorkingProduct((product: productState) => {
			if (!product) { return startingProduct; }

			return {
				...product,
				categories: categories,
			};
		});
	}

	const setAttributeName = (index: number) => (attribute: string) => {
		setWorkingProduct((workingProduct: productState) => {
			if (!workingProduct) { return startingProduct; }

			return {
				...workingProduct,
				attributes: workingProduct.attributes.map((_attribute: any, _index: number) => {
					if (_index === index) {
						_attribute.type = attribute;
					}

					return _attribute;
				}),
			};
		});
	};

	const setAttributeValue = (index: number) => ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setWorkingProduct((workingProduct: productState) => {
			if (!workingProduct) { return startingProduct; }

			return {
				...workingProduct,
				attributes: workingProduct.attributes.map((_attribute: any, _index: number) => {
					if (_index === index) {
						_attribute.value = value;
					}

					return _attribute;
				}),
			};
		});
	};

	function addNewAttribute() {
		setWorkingProduct((workingProduct: productState) => {
			if (!workingProduct) { return startingProduct; }

			return {
				...workingProduct,
				attributes: [
					...(workingProduct.attributes ?? []),
					{ type: '', value: '' },
				],
			};
		});
	}

	function uploadNewImage() {
		imageRef.current?.click();
	}
	function removeImage(index: number) {
		return () => {
			setWorkingProduct((workingProduct: productState) => {
				if (!workingProduct) { return startingProduct; }

				return {
					...workingProduct,
					images: workingProduct.images.filter(
						(img: string, _index: number) => _index !== index
					),
				};
			});
		};
	}
	async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
		if (e.target.files) {
			for (let i = 0; i < e.target.files.length; i++) {
				const file = e.target.files[i];

				if (file) {
					setUploading(true);
					const uploaded = await UploadFile(user!, token!, file);
					setWorkingProduct((product: productState) => {
						if (!product) { return startingProduct; }
						setRequiredFields({ ...requiredFields, image: uploaded ? Validation.normal : Validation.error });
						return {
							...product,
							images: [...product.images, uploaded],
						};
					});
					setUploading(false);
				}
			}
		}

	}

	return (
		<ModalComponent
			closeable={false}
			cancellable={true}
			open={isOpen}
			onRequestClose={onClose}
			title={modalTitle}
			footer={
				<>
					<button onClick={cancel} style={{ marginRight: 16 }}>
						Cancel
					</button>
					<button onClick={finishedAddingProduct} className="primary">
						Done
					</button>
				</>
			}
		>
			<div className="products-modal">
				<div className="row">
					<TextInput
						id="product-title"
						placeholder={'Enter Product Title'}
						label={'Product Title*'}
						onChange={setProductTitle}
						defaultValue={workingProduct?.product_title?.base ?? ''}
						updateValue={workingProduct.product_title?.base}
						shouldUpdateValue={true}
						valid={requiredFields.title}
						onBlur={() => {
							setRequiredFields({ ...requiredFields, title: workingProduct?.product_title?.base ? Validation.normal : Validation.error });
						}}
					/>
					<TagSelectInput
						onChange={setCategories}
						initialTags={workingProduct?.categories ?? []}
						label={'Categories'}
					/>
				</div>
				<Textarea
					placeholder="Describe your product"
					label="About*"
					defaultValue={workingProduct?.description?.base ?? ''}
					onChange={setDescription}
					onBlur={() => {
						setRequiredFields({ ...requiredFields, about: workingProduct?.description?.base ? Validation.normal : Validation.error });
					}}
					valid={requiredFields.about}
				/>
				<div className="product-images">
					{workingProduct &&
						workingProduct?.images?.map(
							(image: string, index: number) => (
								<div
									className={"product-image"}
									key={index}
									style={{
										backgroundImage: `url(${image.replace(
											/ /g,
											'%20'
										)})`,
									}}
								>
									<div className="product-image-actions">
										<button
											className="no-style"
											onClick={removeImage(index)}
										>
											<Icon
												name={ICONS.TRASH}
												size={14}
												color={COLORS.WHITE}
											/>
										</button>
									</div>
								</div>
							)
						)}
					{uploading && (
						<div className="product-image">
							<WaitingIndicator />
						</div>
					)}
					{workingProduct &&
						workingProduct.images.length < 6 &&
						!uploading && (
							<div className={`upload-container ${!workingProduct.images.length ? "empty" : ""}`}>
								<button
									className="add-image"
									onClick={uploadNewImage}
								>
									<Icon
										name={ICONS.ADD}
										size={16}
										color={COLORS.WHITE}
									/>{' '}
									<span>Upload Photo</span>
								</button>
								<p>Accepted file types are .png, .jpeg and up to 50 MB file size</p>
							</div>
						)}
					<input
						type="file"
						style={{ display: 'none' }}
						onChange={handleImageUpload}
						accept=".jpg,.jpeg,.png,.gif"
						ref={imageRef}
						multiple
					/>
				</div>
				<div className="row">
					<TextInput
						id="product-price"
						placeholder={'Enter Product Price'}
						label={'Price'}
						onChange={setPrice}
						onBlur={e => formatCurrency('price', e)}
						defaultValue={workingProduct?.price?.base ?? ''}
						numeric
					/>
					<TextInput
						id="sale-price"
						placeholder={'Enter Product Sale Price'}
						label={'Sale Price'}
						onChange={setSalePrice}
						onBlur={e => formatCurrency('sale_price', e)}
						defaultValue={workingProduct?.sale_price?.base ?? ''}
						numeric
					/>
				</div>
				<div className="row">
					<TextInput
						id="product-url"
						placeholder={'Enter Product Link URL'}
						label={'Buy Now URL'}
						onChange={setBuyNowLink}
						defaultValue={workingProduct?.buy_now_link ?? ''}
					/>
					<TextInput
						id="link-text"
						placeholder={'Enter Product Link Text'}
						label={'Buy Now Button Name'}
						onChange={setBuyNowTitle}
						defaultValue={workingProduct?.button_text?.base ?? ''}
					/>
				</div>
				<div className="field-group switch">
					<Switch
						value={'use-attributes'}
						on={!!workingProduct!.use_attributes}
						onClick={setUseAttributes}
					/>
					<label>Product Attributes</label>
				</div>
				{workingProduct?.use_attributes && (
					<div className="attributes-list">
						{workingProduct.attributes && workingProduct.attributes.length > 0 && (
							<>
								{workingProduct.attributes.map((attribute: any, index: number) => (
									<div
										key={index}
										className="attribute-row"
									>
										<EnterSelectInput
											defaultOptions={attributeOptions?.length ? attributeOptions : ["Color", "Size"]}
											label="Attribute"
											placeholder="Enter attribute"
											onChange={setAttributeName(index)}
											defaultValue={attribute.type}
										/>
										<TextInput
											id={`attribute-value-${index}`}
											placeholder='Attribute value'
											label='Value'
											onChange={setAttributeValue(index)}
											defaultValue={attribute.value}
										/>
									</div>
								))}
							</>
						)}

						<button onClick={addNewAttribute} className="no-style">
							<Icon
								name={ICONS.ADD}
								size={14}
								color={COLORS.WHITE}
							/>{' '}
							Add Attribute
						</button>
					</div>
				)}
			</div>
		</ModalComponent>
	);
}
